/**
 * Maps asset types to abbreviations
 */
const ASSET_TYPE_ABBREVIATIONS = {
  'Totes (Small)': 'TOT',
  'Totes (Medium)': 'TOT',
  'Totes (Large)': 'TOT',
  'Racks': 'RCK',
  'Racks (Large)': 'RCK',
  'Shipping Rack': 'SRK',
  'Material Handling (Rack)': 'MHR',
  'Metal Storage (Rack)': 'MSR',
  'Warehouse (Rack)': 'WRK',
  'Automotive Parts (Rack)': 'APR',
  'Dunnage (Rack)': 'DGR',
  'Steel Rack (Dunnage)': 'SRD',
  'Bins (Small)': 'BIN',
  'Bins (Medium)': 'BIN',
  'Bins (Large)': 'BIN',
  'Returnable (Bin)': 'RTB',
  'Industrial Scrap (Bin)': 'ISB',
  'Metal Scrap Large (Bin)': 'MSL',
  'Metal Scrap Small (Bin)': 'MSS',
  'Metal Stacking (Bin)': 'MSB',
  'Crates': 'CRT',
  'Crates (Small)': 'CRT',
  'Crates (Large)': 'CRT',
  'Pallets': 'PAL',
  'Automotive Packaging (Dunnage)': 'APD',
  'Custom Foam (Dunnage)': 'CFD',
  'Plastic (Dunnage)': 'PLD',
  'Returnable Package (Dunnage)': 'RPD',
};

/**
 * Get abbreviation for an asset type
 * @param {string} assetType - The asset type name (e.g., 'Totes (Small)', 'Racks')
 * @returns {string} - The abbreviation (e.g., 'TOT-S', 'RCK')
 */
function getAssetTypeAbbreviation(assetType) {
  if (!assetType) return 'AST'; // Default abbreviation
  return ASSET_TYPE_ABBREVIATIONS[assetType] || assetType.substring(0, 3).toUpperCase();
}

/**
 * Extract a number from an asset ID or generate one based on the ID
 * @param {string} assetId - The original asset ID
 * @returns {number} - A number to use in the formatted ID
 */
function extractNumberFromAssetId(assetId) {
  if (!assetId) return 1;
  
  // Try to extract numbers from the end of the ID (e.g., "Totes-(Small)-zone-a-1000" -> 1000)
  const match = assetId.match(/(\d+)(?!.*\d)/);
  if (match) {
    return parseInt(match[1], 10);
  }
  
  // If no number found, generate one from the ID hash
  const hash = assetId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return (hash % 999) + 1; // Return a number between 1-999
}

/**
 * Format an asset ID to abbreviated format (e.g., TOT-S-001, RCK-001)
 * @param {Object} asset - The asset object with id and assetType/type properties
 * @returns {string} - The formatted asset ID (e.g., 'TOT-S-001', 'RCK-001')
 */
export function formatAssetId(asset) {
  if (!asset) return 'AST-001';
  
  const assetType = asset.assetType || asset.type || '';
  const abbreviation = getAssetTypeAbbreviation(assetType);
  const number = extractNumberFromAssetId(asset.id);
  
  // Format as ABBREV-001 (3 digits)
  return `${abbreviation}-${number.toString().padStart(3, '0')}`;
}

/**
 * Format an asset ID from just the ID string and type
 * @param {string} assetId - The asset ID string
 * @param {string} assetType - The asset type
 * @returns {string} - The formatted asset ID
 */
export function formatAssetIdFromString(assetId, assetType) {
  const abbreviation = getAssetTypeAbbreviation(assetType);
  const number = extractNumberFromAssetId(assetId);
  return `${abbreviation}-${number.toString().padStart(3, '0')}`;
}

export default formatAssetId;

